/**
 * AssignProjectModal
 * Multi-assignee: a project can carry any number of internal assignees and
 * external delegates at once. Add/remove each independently; each delegate
 * opens the project with their personal guest link (state shown per row).
 * No destructive "this replaces the recipient" gate. Mirrors AssignWorkOrderModal.
 */
import React, { useState } from 'react';
import { Modal, Button, Form, Nav, Alert, Spinner, Badge } from 'react-bootstrap';
import { X } from 'lucide-react';
import ExternalUserSelector from '../../../Components/ExternalUserSelector';
import CreateExternalUserForm from '../../../Components/CreateExternalUserForm';
import InternalUserSelector from '../../../Components/InternalUserSelector';
import AssignmentEmailComposer from '../../../Components/AssignmentEmailComposer';
import CopyGuestLinkButton from '../../../Components/CopyGuestLinkButton';
import ConfirmationModal from '../../../Components/ConfirmationModal';
import { Userprofile } from '../../../Helpers/UsersContext';
import { Delegate, ExternalUser, GuestLinkValidityPayload } from '../../../types/guestAccess';
import { DelegationMeta } from '../../../types/assignees';
import { useAssignmentEmailOptions, AssignmentEmailOptions } from '../../../hooks/useAssignmentEmailOptions';
import { useExternalLink } from '../../../hooks/useExternalLink';
import { useLinkValidityChoice } from '../../../hooks/useLinkValidityChoice';
import { defaultAssignmentMessage, defaultAssignmentSubject } from '../../../utils/assignmentEmail';
import { copyableLinkUrl, describeLinkState, suggestLinkValidity } from '../../../utils/linkValidity';

type Step = 'main' | 'create-user';

interface AssignProjectModalProps {
    show: boolean;
    onHide: () => void;
    internalUsers: Userprofile[];
    externalUsers?: ExternalUser[];
    assigneeUids: string[];
    delegations: Record<string, DelegationMeta>;
    /** Current delegates with link state (useDelegates), keyed by externalUserId */
    delegatesByUid?: Record<string, Delegate>;
    projectKey: string;
    projectTitle: string;
    /** Planned end (YYYY-MM-DD), for the suggested link validity */
    projectDueDate?: string;
    buildingName?: string;
    getUserName: (uid: string) => string;
    /** Single-select compat (unused by multi UI but kept for callers). */
    onAssignInternal: (userId: string | null) => Promise<void>;
    onAddInternal: (userId: string, sendEmail?: boolean) => Promise<void>;
    onRemoveInternal: (userId: string) => Promise<void>;
    onAssignExternal: (externalUser: ExternalUser, emailOptions: AssignmentEmailOptions, linkValidity: GuestLinkValidityPayload) => Promise<void>;
    onRevokeExternal: (externalUserId: string) => Promise<void>;
    onAssigned: () => void;
    onExternalUserCreated?: () => void;
}

const AssignProjectModal: React.FC<AssignProjectModalProps> = ({
    show,
    onHide,
    internalUsers,
    externalUsers,
    assigneeUids,
    delegations,
    delegatesByUid = {},
    projectKey,
    projectTitle,
    projectDueDate,
    buildingName,
    getUserName,
    onAddInternal,
    onRemoveInternal,
    onAssignExternal,
    onRevokeExternal,
    onAssigned,
    onExternalUserCreated,
}) => {
    const [step, setStep] = useState<Step>('main');
    const [activeTab, setActiveTab] = useState<'internal' | 'external'>('internal');
    const [selectedInternalUser, setSelectedInternalUser] = useState('');
    const [selectedExternalUser, setSelectedExternalUser] = useState<ExternalUser | null>(null);
    const [sendEmail, setSendEmail] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingRemoval, setPendingRemoval] = useState<
        | { kind: 'internal'; uid: string; name: string }
        | { kind: 'external'; uid: string; name: string }
        | null
    >(null);

    const delegateUids = Object.keys(delegations);

    const emailDefaults = {
        subject: defaultAssignmentSubject('project', projectTitle, buildingName),
        message: selectedExternalUser
            ? defaultAssignmentMessage('project', selectedExternalUser.name)
            : '',
    };
    const email = useAssignmentEmailOptions(show, show && activeTab === 'external', emailDefaults);
    // The selected user's personal link and tasks drive the composer's link
    // block: the validity is pre-filled from the latest due date (+14 days).
    const selectedLink = useExternalLink(show && selectedExternalUser ? selectedExternalUser.uid : null);
    const suggestion = suggestLinkValidity(selectedLink.info?.tasks ?? [], { title: projectTitle, dueDate: projectDueDate });
    const validity = useLinkValidityChoice(selectedLink.info?.link, selectedExternalUser?.uid ?? null, suggestion);
    // The validity sent depends on the user's current link, so nothing is
    // submitted until it is known (a default would overwrite the link's validity).
    const linkKnown = !selectedLink.loading && selectedLink.info !== null;

    const handleClose = () => {
        if (!submitting) {
            setStep('main');
            setError(null);
            setSelectedInternalUser('');
            setSelectedExternalUser(null);
            onHide();
        }
    };

    const handleUserCreated = (user: ExternalUser) => {
        setSelectedExternalUser(user);
        setStep('main');
        onExternalUserCreated?.();
    };

    const run = async (fn: () => Promise<void>, fallback: string) => {
        setError(null);
        setSubmitting(true);
        try {
            await fn();
            onAssigned();
        } catch (err) {
            setError(err instanceof Error ? err.message : fallback);
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddInternal = () =>
        selectedInternalUser &&
        run(async () => {
            await onAddInternal(selectedInternalUser, sendEmail);
            setSelectedInternalUser('');
        }, 'Kunde inte tilldela');

    const handleAddExternal = () =>
        selectedExternalUser &&
        run(async () => {
            await onAssignExternal(selectedExternalUser, email.options, validity.payload);
            setSelectedExternalUser(null);
        }, 'Kunde inte skicka inbjudan');

    // Run the pending removal after the user confirms.
    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        const removal = pendingRemoval;
        await run(
            () => (removal.kind === 'internal' ? onRemoveInternal(removal.uid) : onRevokeExternal(removal.uid)),
            removal.kind === 'internal' ? 'Kunde inte ta bort tilldelad' : 'Kunde inte ta bort extern mottagare'
        );
        setPendingRemoval(null);
    };

    return (
        <>
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 'create-user' ? 'Lägg till extern användare' : 'Tilldela projekt'}
                </Modal.Title>
            </Modal.Header>

            {step === 'create-user' ? (
                <CreateExternalUserForm onUserCreated={handleUserCreated} onCancel={() => setStep('main')} />
            ) : (
                <>
                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link active={activeTab === 'internal'} onClick={() => setActiveTab('internal')}>
                                    Interna{assigneeUids.length > 0 ? ` (${assigneeUids.length})` : ''}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link active={activeTab === 'external'} onClick={() => setActiveTab('external')}>
                                    Externa{delegateUids.length > 0 ? ` (${delegateUids.length})` : ''}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        {activeTab === 'internal' && (
                            <Form.Group>
                                <Form.Label>Tilldelade</Form.Label>
                                {assigneeUids.length === 0 ? (
                                    <p className="text-muted small">Inga interna mottagare ännu.</p>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {assigneeUids.map((uid) => (
                                            <Badge key={uid} bg="secondary" className="d-flex align-items-center gap-1 p-2">
                                                {getUserName(uid)}
                                                <X
                                                    size={14}
                                                    role="button"
                                                    aria-label={`Ta bort ${getUserName(uid)}`}
                                                    onClick={() => !submitting && setPendingRemoval({ kind: 'internal', uid, name: getUserName(uid) })}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                                <Form.Label>Lägg till intern mottagare</Form.Label>
                                <div className="d-flex gap-2">
                                    <InternalUserSelector
                                        users={internalUsers}
                                        value={selectedInternalUser}
                                        onChange={setSelectedInternalUser}
                                        disabled={submitting}
                                        disabledIds={assigneeUids}
                                        unassignedLabel="Välj användare…"
                                    />
                                    <Button variant="primary" onClick={handleAddInternal} disabled={submitting || !selectedInternalUser}>
                                        {submitting ? <Spinner animation="border" size="sm" /> : 'Lägg till'}
                                    </Button>
                                </div>
                                <Form.Check
                                    type="checkbox"
                                    id="assign-send-email-project"
                                    className="mt-3"
                                    label="Skicka e-postavisering till mottagaren"
                                    checked={sendEmail}
                                    onChange={(e) => setSendEmail(e.target.checked)}
                                    disabled={submitting}
                                />
                            </Form.Group>
                        )}

                        {activeTab === 'external' && (
                            <>
                                <Form.Label>Externa mottagare</Form.Label>
                                {delegateUids.length === 0 ? (
                                    <p className="text-muted small">Inga externa mottagare ännu.</p>
                                ) : (
                                    <div className="mb-3">
                                        {delegateUids.map((uid) => (
                                            <div key={uid} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                                                <span className="d-flex flex-column">
                                                    <span>{getUserName(uid)}</span>
                                                    {delegatesByUid[uid] && (
                                                        <small className="text-muted">{describeLinkState(delegatesByUid[uid].link)}</small>
                                                    )}
                                                </span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <CopyGuestLinkButton url={copyableLinkUrl(delegatesByUid[uid]?.link)} />
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => setPendingRemoval({ kind: 'external', uid, name: getUserName(uid) })}
                                                        disabled={submitting}
                                                    >
                                                        Ta bort
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Form.Group className="mb-3">
                                    <Form.Label>Lägg till extern mottagare</Form.Label>
                                    <ExternalUserSelector
                                        onSelect={setSelectedExternalUser}
                                        onCreateNew={() => setStep('create-user')}
                                        selectedUser={selectedExternalUser}
                                        users={externalUsers}
                                        disabledIds={delegateUids}
                                    />
                                </Form.Group>
                                {selectedExternalUser && (
                                    <>
                                        <Alert variant="info">
                                            En e-post med länk skickas till {selectedExternalUser.email}
                                        </Alert>
                                        <AssignmentEmailComposer
                                            options={email.options}
                                            onSubjectChange={email.setSubject}
                                            onCustomMessageChange={email.setCustomMessage}
                                            onReplyToChange={email.setReplyTo}
                                            onSendCopyToAdminChange={email.setSendCopyToAdmin}
                                            taskType="project"
                                            taskTitle={projectTitle}
                                            buildingName={buildingName}
                                            loadingDefault={email.loadingDefault}
                                            disabled={submitting}
                                            idSuffix="project"
                                            recipientName={selectedExternalUser.name}
                                            taskKey={projectKey}
                                            linkInfo={linkKnown ? selectedLink.info : null}
                                            link={validity}
                                            linkLoading={selectedLink.loading}
                                            linkError={linkKnown ? null : selectedLink.error}
                                            onRetryLink={() => { selectedLink.clearError(); void selectedLink.refetch(); }}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                            Stäng
                        </Button>
                        {activeTab === 'external' && (
                            <Button variant="primary" onClick={handleAddExternal} disabled={submitting || !selectedExternalUser || !linkKnown || !validity.complete}>
                                {submitting ? <Spinner animation="border" size="sm" /> : 'Skicka inbjudan'}
                            </Button>
                        )}
                    </Modal.Footer>
                </>
            )}
        </Modal>

        <ConfirmationModal
            show={!!pendingRemoval}
            title={pendingRemoval?.kind === 'external' ? 'Återkalla delegering' : 'Ta bort tilldelning'}
            message={
                pendingRemoval?.kind === 'external'
                    ? `Vill du återkalla åtkomsten för ${pendingRemoval.name}? Projektet försvinner från deras portal.`
                    : pendingRemoval
                        ? `Vill du ta bort ${pendingRemoval.name} från projektet?`
                        : ''
            }
            confirmText={pendingRemoval?.kind === 'external' ? 'Återkalla' : 'Ta bort'}
            loading={submitting}
            onConfirm={confirmRemoval}
            onCancel={() => setPendingRemoval(null)}
        />
        </>
    );
};

export default AssignProjectModal;
